import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  quotesApi,
  type QuoteDetailDto,
  type QuoteItemDto,
  type QuoteInclusionDto,
  type UpdateQuoteItemRequest,
} from "@/lib/quotes";
import { quoteInclusionsAdminApi, type StandardInclusionRow } from "@/lib/quote-inclusions-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { BuildPricePanel } from "@/components/bom/BuildPricePanel";
import { Pencil, X, Plus, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";

const fmt = new Intl.NumberFormat("en-AU", { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 });
function formatCurrency(v: number | null | undefined): string {
  return v == null ? "—" : fmt.format(v);
}

interface DraftState {
  description: string;
  quantity: number;
  unitPriceExGst: number;
}

export interface QuoteOrderDetailsCardProps {
  quote: QuoteDetailDto;
  onAddFromProduct: () => void;
  onAddManufacturing?: () => void;
  onEditManufacturing?: (mfgBomId: number) => void;
}

/**
 * Middle-column Order Details card: items + options + includes lists.
 * Extracted from the old QuoteHeroForm middle column.
 *
 * Built lines (sourceBomId != null): expand to inline BuildPricePanel,
 * read-only qty/price, no inline Edit — only Delete.
 * Manual lines (sourceBomId == null): keep inline Edit/Delete as before.
 */
export function QuoteOrderDetailsCard({ quote, onAddFromProduct, onAddManufacturing, onEditManufacturing }: QuoteOrderDetailsCardProps) {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<DraftState>({ description: "", quantity: 0, unitPriceExGst: 0 });
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const editable = ["Draft", "Sent"].includes(quote.status);

  const itemsQ = useQuery({
    queryKey: ["quote-items", quote.quoteId],
    queryFn: () => quotesApi.items(quote.quoteId),
  });
  const inclusionsQ = useQuery({
    queryKey: ["quote-inclusions", quote.quoteId],
    queryFn: () => quotesApi.inclusions(quote.quoteId),
  });
  const libraryQ = useQuery({
    queryKey: ["quote-inclusions-library"],
    queryFn: () => quoteInclusionsAdminApi.listActive(),
    enabled: editable,
  });

  const items: QuoteItemDto[] = itemsQ.data ?? [];
  const inclusions: QuoteInclusionDto[] = inclusionsQ.data ?? [];
  const attachedLibIds = new Set(inclusions.map((i) => i.standardInclusionId));
  const availableLibrary: StandardInclusionRow[] = (libraryQ.data ?? []).filter(
    (l) => !attachedLibIds.has(l.standardInclusionId),
  );

  const attachMut = useMutation({
    mutationFn: (standardInclusionId: number) =>
      quotesApi.attachInclusions(quote.quoteId, [standardInclusionId]),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["quote-inclusions", quote.quoteId] }),
    onError: (err: unknown) => toast.error(`Failed to add inclusion: ${(err as Error).message}`),
  });
  const detachMut = useMutation({
    mutationFn: (quoteInclusionId: number) =>
      quotesApi.detachInclusion(quote.quoteId, quoteInclusionId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["quote-inclusions", quote.quoteId] }),
    onError: (err: unknown) => toast.error(`Failed to remove inclusion: ${(err as Error).message}`),
  });

  const delMut = useMutation({
    mutationFn: (itemId: number) => quotesApi.deleteItem(quote.quoteId, itemId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote-items", quote.quoteId] });
      void qc.invalidateQueries({ queryKey: ["quote", quote.quoteId] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error("This quote changed since you opened it — reloading the latest.");
        void qc.invalidateQueries({ queryKey: ["quote", quote.quoteId] });
        return;
      }
      toast.error(`Delete failed: ${(err as Error).message}`);
    },
  });

  const patchMut = useMutation({
    mutationFn: (v: { itemId: number; body: UpdateQuoteItemRequest }) =>
      quotesApi.patchItem(quote.quoteId, v.itemId, v.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote-items", quote.quoteId] });
      void qc.invalidateQueries({ queryKey: ["quote", quote.quoteId] });
      setEditingId(null);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error("This quote changed since you opened it — reloading the latest.");
        void qc.invalidateQueries({ queryKey: ["quote", quote.quoteId] });
        return;
      }
      toast.error(`Update failed: ${(err as Error).message}`);
    },
  });

  // "Calculate" — quote totals are recomputed server-side on every line change;
  // this button forces a refresh/resync so the card reflects the latest server totals.
  const recalcMut = useMutation({
    mutationFn: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["quote-items", quote.quoteId] }),
        qc.invalidateQueries({ queryKey: ["quote", quote.quoteId] }),
        qc.invalidateQueries({ queryKey: ["quote-inclusions", quote.quoteId] }),
      ]),
    onSuccess: () => toast.success("Totals recalculated"),
    onError: (err: unknown) => toast.error(`Couldn't recalculate: ${(err as Error).message}`),
  });

  function startEdit(item: QuoteItemDto) {
    setDraft({
      description: item.description ?? "",
      quantity: item.quantity,
      unitPriceExGst: item.unitPriceExGst,
    });
    setEditingId(item.quoteItemId);
  }

  function handleSave(item: QuoteItemDto) {
    if (item.rowVersionBase64 == null) return;
    patchMut.mutate({
      itemId: item.quoteItemId,
      body: {
        sortOrder: item.sortOrder,
        description: draft.description,
        quantity: draft.quantity,
        unitPriceExGst: draft.unitPriceExGst,
        notes: item.notes ?? null,
        rowVersionBase64: item.rowVersionBase64,
      },
    });
  }

  async function addManualLine() {
    try {
      const created = await quotesApi.addItem(quote.quoteId, {
        description: "",
        quantity: 1,
        unitPriceExGst: 0,
      });
      void qc.invalidateQueries({ queryKey: ["quote-items", quote.quoteId] });
      void qc.invalidateQueries({ queryKey: ["quote", quote.quoteId] });
      startEdit(created);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast.error("This quote changed since you opened it — reloading the latest.");
        void qc.invalidateQueries({ queryKey: ["quote", quote.quoteId] });
        return;
      }
      toast.error(`Failed to add item: ${(e as Error).message}`);
    }
  }

  return (
    <Card className="border border-slate-400 rounded-xl overflow-hidden bg-white shadow-sm">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-2 px-4 flex flex-row items-center justify-between">
        <CardTitle
          role="heading"
          aria-level={3}
          className="text-xs font-bold text-slate-800 uppercase tracking-wider m-0"
        >
          Order Details
        </CardTitle>
        {editable && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" type="button" className="h-6 px-3 text-[10px] rounded-full border-transparent bg-info/15 text-primary hover:bg-info/25 font-semibold uppercase tracking-wider">
                <Plus className="mr-1 h-3 w-3" /> Add item
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onAddFromProduct()}>From a product</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => void addManualLine()}>Manual line</DropdownMenuItem>
              {onAddManufacturing ? (
                <DropdownMenuItem onSelect={() => onAddManufacturing()}>Manufacturing item</DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </CardHeader>
      <CardContent className="space-y-4 p-4">

        <div className="space-y-0">
          {itemsQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading items…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No items yet.</p>
          ) : (
            <div className="flex flex-col">
              <div 
                className="grid items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest py-1 px-2 border-b border-slate-100 mb-0.5 bg-slate-50/50 rounded-t-sm"
                style={{ gridTemplateColumns: "minmax(120px, 1fr) 50px 70px 80px 50px" }}
              >
                <div>Description</div>
                <div className="text-right">Qty</div>
                <div className="text-right">Price</div>
                <div className="text-right pr-2">Total</div>
                <div></div>
              </div>
              
              <div className="space-y-0">
                {items.map((item) => {
                  const isBuilt = item.sourceBomId != null;

                  if (isBuilt) {
                    const isExpanded = expandedId === item.quoteItemId;
                    return (
                      <div key={item.quoteItemId} className="flex flex-col text-sm rounded-sm hover:bg-slate-50/50 transition-colors">
                        <div 
                          className="grid items-center gap-2 text-[#1a2f4c] py-1 px-2 group"
                          style={{ gridTemplateColumns: "minmax(120px, 1fr) 50px 70px 80px 50px" }}
                        >
                          <button
                            type="button"
                            className="text-left transition-colors break-words leading-tight flex items-center gap-2"
                            onClick={() => setExpandedId(isExpanded ? null : item.quoteItemId)}
                          >
                            <span className="font-semibold truncate group-hover:text-[#1a2f4c] transition-colors">{item.description || "(no description)"}</span>
                            <span className="shrink-0 text-slate-400 group-hover:text-[#1a2f4c] transition-colors ml-1">
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </span>
                          </button>
                          <div className="text-right text-slate-600 font-medium">{item.quantity}</div>
                          <div className="text-right text-slate-500">{formatCurrency(item.unitPriceExGst)}</div>
                          <div className="text-right font-semibold pr-2">{formatCurrency(item.lineTotalExGst)}</div>
                          <div className="flex justify-end items-center">
                            {editable && (
                              <button
                                type="button"
                                className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                aria-label={`Delete ${item.description}`}
                                disabled={delMut.isPending}
                                onClick={() => delMut.mutate(item.quoteItemId)}
                              >
                                <X className="h-4 w-4" strokeWidth={2.5} />
                              </button>
                            )}
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="px-2 pb-2 pt-1">
                            <BuildPricePanel
                              bomId={item.sourceBomId!}
                              quoteId={quote.quoteId}
                              disabled={!editable}
                            />
                          </div>
                        )}
                      </div>
                    );
                  }

                  // ---- Manufacturing (tree BOM) line — read-only totals, "Edit build" reopens the builder ----
                  if (item.sourceMfgBomId != null) {
                    return (
                      <div
                        key={item.quoteItemId}
                        className="grid items-center gap-2 text-sm text-[#1a2f4c] py-1 px-2 hover:bg-slate-50/50 transition-colors rounded-sm group"
                        style={{ gridTemplateColumns: "minmax(120px, 1fr) 50px 70px 80px 50px" }}
                      >
                        <span className="font-semibold truncate break-words leading-tight">{item.description || "(no description)"}</span>
                        <div className="text-right text-slate-600 font-medium">{item.quantity}</div>
                        <div className="text-right text-slate-500">{formatCurrency(item.unitPriceExGst)}</div>
                        <div className="text-right font-semibold pr-2">{formatCurrency(item.lineTotalExGst)}</div>
                        <div className="flex justify-end gap-1 items-center">
                          {editable && (
                            <>
                              {onEditManufacturing && (
                                <button
                                  type="button"
                                  className="text-slate-300 hover:text-blue-500 transition-colors p-1"
                                  aria-label={`Edit build ${item.description}`}
                                  onClick={() => onEditManufacturing(item.sourceMfgBomId!)}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              )}
                              <button
                                type="button"
                                className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                aria-label={`Delete ${item.description}`}
                                disabled={delMut.isPending}
                                onClick={() => delMut.mutate(item.quoteItemId)}
                              >
                                <X className="h-4 w-4" strokeWidth={2.5} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // ---- Manual line (no BOM) ------------------------------------
                  return editingId === item.quoteItemId ? (
                    <div 
                      key={item.quoteItemId} 
                      className="grid items-center gap-2 text-sm text-[#1a2f4c] py-1 px-2 bg-slate-50 rounded-sm shadow-inner"
                      style={{ gridTemplateColumns: "minmax(120px, 1fr) 50px 70px 80px 50px" }}
                    >
                      <div>
                        <Input
                          aria-label="Description"
                          value={draft.description}
                          onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                          className="h-6 text-xs px-2 bg-white border-slate-200"
                        />
                      </div>
                      <Input
                        aria-label="Quantity"
                        type="number"
                        value={draft.quantity}
                        onChange={(e) => setDraft((d) => ({ ...d, quantity: Number(e.target.value) }))}
                        className="h-6 text-xs text-right px-1 bg-white border-slate-200"
                      />
                      <Input
                        aria-label="Unit Price"
                        type="number"
                        value={draft.unitPriceExGst}
                        onChange={(e) => setDraft((d) => ({ ...d, unitPriceExGst: Number(e.target.value) }))}
                        className="h-6 text-xs text-right px-1 bg-white border-slate-200"
                      />
                      <div className="text-right text-slate-500 font-medium text-xs pr-2">{formatCurrency(draft.quantity * draft.unitPriceExGst)}</div>
                      <div className="flex flex-col gap-1 justify-center items-end">
                        <Button
                          variant="ghost" size="sm" type="button"
                          className="h-5 px-2 text-[10px] font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50/50"
                          disabled={item.rowVersionBase64 == null || patchMut.isPending}
                          onClick={() => handleSave(item)}
                        >
                          SAVE
                        </Button>
                        <Button
                          variant="ghost" size="sm" type="button"
                          className="h-5 px-2 text-[10px] font-semibold text-slate-400 hover:text-slate-600 hover:bg-slate-200/50"
                          onClick={() => setEditingId(null)}
                        >
                          CANCEL
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      key={item.quoteItemId} 
                      className="grid items-center gap-2 text-sm text-[#1a2f4c] py-1 px-2 hover:bg-slate-50/50 transition-colors rounded-sm group"
                      style={{ gridTemplateColumns: "minmax(120px, 1fr) 50px 70px 80px 50px" }}
                    >
                      <button
                        type="button"
                        className="text-left font-semibold truncate group-hover:text-[#1a2f4c] transition-colors break-words leading-tight"
                        onClick={() => { if (editable) startEdit(item); }}
                      >
                        {item.description || "(no description)"}
                      </button>
                      <div className="text-right text-slate-600 font-medium">{item.quantity}</div>
                      <div className="text-right text-slate-500">{formatCurrency(item.unitPriceExGst)}</div>
                      <div className="text-right font-semibold pr-2">{formatCurrency(item.lineTotalExGst)}</div>
                      <div className="flex justify-end gap-1 items-center">
                        {editable && (
                          <>
                            <button
                              type="button"
                              className="text-slate-300 hover:text-blue-500 transition-colors p-1"
                              aria-label={`Edit ${item.description}`}
                              onClick={() => startEdit(item)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              className="text-slate-300 hover:text-red-500 transition-colors p-1"
                              aria-label={`Delete ${item.description}`}
                              disabled={delMut.isPending}
                              onClick={() => delMut.mutate(item.quoteItemId)}
                            >
                              <X className="h-4 w-4" strokeWidth={2.5} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Inclusions</Label>
            {editable && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" type="button" className="h-6 px-3 text-[10px] rounded-full border-transparent bg-info/15 text-primary hover:bg-info/25 font-semibold uppercase tracking-wider">
                    <Plus className="mr-1 h-3 w-3" /> Add inclusion
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-h-64 overflow-auto">
                  {availableLibrary.length === 0 ? (
                    <DropdownMenuItem disabled>No inclusions available</DropdownMenuItem>
                  ) : (
                    availableLibrary.map((lib) => (
                      <DropdownMenuItem
                        key={lib.standardInclusionId}
                        onSelect={() => attachMut.mutate(lib.standardInclusionId)}
                      >
                        {lib.name}
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {inclusionsQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading inclusions…</p>
          ) : inclusions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No inclusions added.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {inclusions.map((inc) => (
                <li key={inc.quoteInclusionId} className="flex items-start justify-between gap-2">
                  <span className="whitespace-pre-wrap">{inc.bodyTextSnapshot}</span>
                  {editable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      aria-label="Remove inclusion"
                      disabled={detachMut.isPending}
                      onClick={() => detachMut.mutate(inc.quoteInclusionId)}
                    >
                      Remove
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-end pt-4 mt-6 border-t border-slate-100">
          <Button variant="outline" type="button" className="rounded-full border-transparent bg-info/15 text-primary hover:bg-info/25 font-semibold px-4 h-8 text-xs" disabled={recalcMut.isPending} onClick={() => recalcMut.mutate()}>
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${recalcMut.isPending ? "animate-spin" : ""}`} /> Calculate
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
